import { assert, assertNever, CheckedAddress, raise } from '@sparkdotfi/common-universal'

import { DepositToWethAction } from '../../deposit-to-weth/types'
import { DepositToSavingsAction, DepositToSavingsObjective } from '../types'
import { getSavingsDepositActionPath } from './getSavingsDepositActionPath'
import {
  migrationActionsConfig,
  psm3Address,
  usdcVaultAddress,
  usdsPsmActionsConfig,
} from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getApproveActions } from '@/features/actions/logic/getApproveActions'
import { Action, ActionContext } from '@/features/actions/logic/types'

export function createDepositToSavingsActions(objective: DepositToSavingsObjective, context: ActionContext): Action[] {
  const tokenRepository = context.tokenRepository ?? raise('Tokens info is required for deposit to savings action')
  const chainId = context.chainId

  assert(context.savingsAccounts, 'Savings account repository info is required for deposit to savings action')
  const depositAction: DepositToSavingsAction = {
    type: 'depositToSavings',
    value: objective.value,
    token: objective.token,
    savingsToken: objective.savingsToken,
    underlyingToken: context.savingsAccounts.findOneBySavingsToken(objective.savingsToken).underlyingToken,
    displayMode: objective.displayMode,
  }
  function getSpenderApproveActions(spender: CheckedAddress): Action[] {
    return getApproveActions({
      token: objective.token,
      spender,
      value: objective.value,
      context,
    })
  }

  const actionPath = getSavingsDepositActionPath({
    token: objective.token,
    savingsToken: objective.savingsToken,
    chainId,
  })

  switch (actionPath) {
    case 'sexy-dai-to-sdai':
      return [depositAction]

    case 'usdc-to-susds':
      return [...getSpenderApproveActions(getContractAddress(usdsPsmActionsConfig.address, chainId)), depositAction]

    case 'dai-to-susds':
      return [...getSpenderApproveActions(getContractAddress(migrationActionsConfig.address, chainId)), depositAction]

    case 'usdc-to-spusdc':
    case 'usdt-to-spusdt':
    case 'weth-to-speth':
    case 'pyusd-to-sppyusd':
    case 'dai-to-sdai':
    case 'usds-to-susds':
    case 'usds-to-stusds':
    case 'avalanche-usdc-to-spusdc':
      return [...getSpenderApproveActions(objective.savingsToken.address), depositAction]

    case 'eth-to-speth': {
      const depositToWethAction: DepositToWethAction = {
        type: 'depositToWeth',
        eth: objective.token,
        value: objective.value,
      }

      const weth = tokenRepository.findOneBySymbol(TokenSymbol('WETH'))

      const approveActions = getApproveActions({
        token: weth,
        spender: objective.savingsToken.address,
        value: objective.value,
        context,
      })

      const depositAction: DepositToSavingsAction = {
        type: 'depositToSavings',
        token: weth,
        value: objective.value,
        savingsToken: objective.savingsToken,
        underlyingToken: weth,
      }

      return [depositToWethAction, ...approveActions, depositAction]
    }

    case 'usdc-to-susdc':
    case 'base-usdc-to-susdc':
    case 'arbitrum-usdc-to-susdc':
    case 'optimism-usdc-to-susdc':
    case 'unichain-usdc-to-susdc':
      return [...getSpenderApproveActions(getContractAddress(usdcVaultAddress, chainId)), depositAction]

    case 'base-usdc-to-susds':
    case 'base-usds-to-susds':
    case 'arbitrum-usds-to-susds':
    case 'arbitrum-usdc-to-susds':
    case 'optimism-usds-to-susds':
    case 'optimism-usdc-to-susds':
    case 'unichain-usds-to-susds':
    case 'unichain-usdc-to-susds':
      return [...getSpenderApproveActions(getContractAddress(psm3Address, chainId)), depositAction]

    default:
      assertNever(actionPath)
  }
}
